import { useCallback, useEffect, useState } from "react"

export const isFullscreenActive = () => {
  return !!(
    document.fullscreenElement ||
    document.webkitFullscreenElement ||
    document.mozFullScreenElement ||
    document.msFullscreenElement
  )
}

export const exitFullscreen = () => {
  const fsElement =
    document.fullscreenElement ||
    document.webkitFullscreenElement ||
    document.mozFullScreenElement ||
    document.msFullscreenElement

  if (fsElement) {
    if (document.exitFullscreen) {
      document.exitFullscreen().catch(() => {})
    } else if (document.webkitExitFullscreen) {
      document.webkitExitFullscreen()
    } else if (document.mozCancelFullScreen) {
      document.mozCancelFullScreen()
    } else if (document.msExitFullscreen) {
      document.msExitFullscreen()
    }
  }
}

export const enterFullscreen = (target = document.documentElement) => {
  if (!isFullscreenActive()) {
    const el = target || document.documentElement
    if (el.requestFullscreen) {
      el.requestFullscreen().catch((err) => {
        console.error("Error attempting to enable fullscreen:", err)
      })
    } else if (el.webkitRequestFullscreen) {
      el.webkitRequestFullscreen()
    } else if (el.mozRequestFullScreen) {
      el.mozRequestFullScreen()
    } else if (el.msRequestFullscreen) {
      el.msRequestFullscreen()
    }
  }
}

export const toggleFullscreen = (target = document.documentElement) => {
  if (isFullscreenActive()) {
    exitFullscreen()
  } else {
    enterFullscreen(target)
  }
}

export const useFullscreen = (targetRef = null) => {
  const [isFullscreen, setIsFullscreen] = useState(isFullscreenActive)

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(isFullscreenActive())
    }

    document.addEventListener("fullscreenchange", handleFullscreenChange)
    document.addEventListener("webkitfullscreenchange", handleFullscreenChange)
    document.addEventListener("mozfullscreenchange", handleFullscreenChange)
    document.addEventListener("MSFullscreenChange", handleFullscreenChange)

    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange)
      document.removeEventListener(
        "webkitfullscreenchange",
        handleFullscreenChange
      )
      document.removeEventListener(
        "mozfullscreenchange",
        handleFullscreenChange
      )
      document.removeEventListener("MSFullscreenChange", handleFullscreenChange)
    }
  }, [])

  const handleToggle = useCallback(() => {
    const target = targetRef?.current || document.documentElement
    toggleFullscreen(target)
  }, [targetRef])

  const handleEnter = useCallback(() => {
    const target = targetRef?.current || document.documentElement
    enterFullscreen(target)
  }, [targetRef])

  const handleExit = useCallback(() => {
    exitFullscreen()
  }, [])

  return {
    isFullscreen,
    toggleFullscreen: handleToggle,
    enterFullscreen: handleEnter,
    exitFullscreen: handleExit,
  }
}

export default useFullscreen
